export interface SeriesPoint { at: number; value: number }

const WIDTH = 800;
const HEIGHT = 160;
const TOP = 16;
const BOTTOM = 12;

/** Balance-over-time area chart. Purely presentational: the series is computed by the caller. */
export function Sparkline({ points, label }: { points: SeriesPoint[]; label: string }) {
  if (points.length < 2) return null;
  const values = points.map((point) => point.value);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min;
  const plot = HEIGHT - TOP - BOTTOM;
  const start = points[0].at;
  const timeSpan = points[points.length - 1].at - start;

  const coordinates = points.map((point, index) => {
    // X is proportional to TIME, not to the point's index: equal index spacing stretched a burst
    // of same-day movements across weeks of axis and compressed real gaps, so the curve did not
    // correspond to the dates under it. Movements seconds apart now draw as the vertical step
    // they are. A zero time span (all points in the same instant) falls back to index spacing.
    const x = timeSpan === 0
      ? (index / (points.length - 1)) * WIDTH
      : ((point.at - start) / timeSpan) * WIDTH;
    // A flat series has no span to scale against, so it sits on the middle line.
    const y = span === 0 ? TOP + plot / 2 : HEIGHT - BOTTOM - ((point.value - min) / span) * plot;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const line = `M${coordinates.join(" L")}`;
  const last = coordinates[coordinates.length - 1].split(",");

  return (
    <svg className="sparkline" viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none" role="img" aria-label={label}>
      <defs>
        <linearGradient id="sparkline-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#0179E2" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#0179E2" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${line} L${WIDTH},${HEIGHT} L0,${HEIGHT} Z`} fill="url(#sparkline-fill)" />
      <path d={line} stroke="#0179E2" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      <circle cx={last[0]} cy={last[1]} r="6" fill="#3DFFDF" stroke="#fff" strokeWidth="3" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
